import {ReportDefinitionV1Tasks} from '../contracts/report-definition.types';
import {GroupingToken} from '../ReportTable/useClientGrouping';
import {ReportDataResult} from './reportRowModel';
import {SavedReportRef} from './savedReportInput';

/** Where a report sits in the publish lifecycle, as report-service reports it. */
export type ReportDraftState = 'PUBLISHED' | 'DRAFT' | 'UNPUBLISHED';

/** A saved report as the builder needs it on re-open: the definition to render, the provenance that
 * decides whether the surface shows an AI banner and what prompt it quotes, the lifecycle state the
 * banner and the Discard action key off, and the sharing flag every update has to echo back. */
export interface LoadedReport {
	definition: ReportDefinitionV1Tasks;
	source: 'AI' | 'STANDARD';
	promptText: string | null;
	/** Whether this report has been hand-edited (conditions or reporting period) since it was generated
	 * from its prompt. Drives the AI banner's second state. False when the stored record carries no
	 * boolean: a report predating the flag has no known manual edit, which is what the original wording
	 * already claims. */
	manuallyEdited: boolean;
	/** What the logged-in person may do with this report. Resolved by the backend, which is the same
	 * place the rule is enforced, so the surface never has to re-derive it. */
	viewerPermission: ViewerPermission;
	/** The stored report's lifecycle state, narrowed to the known union. Null when the stored record
	 * carries no state or one this client does not recognise — consumers must read a null as "no
	 * reader isolation proven" rather than inventing one. Without this a re-opened published report
	 * would start as UNPUBLISHED, so the banner would claim it had never been published and Discard
	 * would be permanently disabled. */
	draftState: ReportDraftState | null;
	/** When the report was last published, or null when it never was. Drives view mode's
	 * "Published {date}" line and `beginEdit`'s conservative fallback. */
	publishedAt: string | null;
	/** Whether the stored report is private to its owner. Read back rather than guessed: an update
	 * overwrites this field unconditionally, so a guessed value would silently flip a report between
	 * private and shared. Null when the stored record does not carry a boolean, in which case there is
	 * no truthful value to echo and the caller must not invent one. */
	privateAccess: boolean | null;
	/** Whether discarding the currently open draft would also revert the report's filter. False
	 * (including when no draft is open, or the stored record predates this field) means discard leaves
	 * filter live, exactly as today. Drives whether the discard-confirmation copy mentions filter. */
	draftFilterWillRevert: boolean;
}

/** What every lifecycle call resolves with. `locked` is true when another user holds the edit lock,
 * in which case nothing changed server-side and the other two fields are null. */
export interface ReportLifecycleState {
	draftState: ReportDraftState | null;
	publishedAt: string | null;
	locked: boolean;
	/** See `LoadedReport.draftFilterWillRevert` — same meaning, refreshed by whichever lifecycle call
	 * (enterDraft/publish/discard) this state came from. */
	draftFilterWillRevert: boolean;
}

export type ViewerPermission = 'OWNER' | 'EDITOR' | 'VIEWER';

/** Thrown by `exportCsv` when the backend refuses the export because the report has more rows than it
 * will order. Part of the interface rather than of one implementation, so the surface can word that case
 * differently without importing the HTTP transport, and any other implementation can raise it too.
 *
 * A type rather than a message or a status code on a generic error: nothing downstream should branch on
 * a string, because a copy change would silently fold this case back into the generic failure — which
 * would tell the user to retry something that cannot succeed. */
export class ReportExportTooLargeError extends Error {
	constructor() {
		super('the report has too many rows to export');
		this.name = 'ReportExportTooLargeError';
	}
}

export interface ReportDataSource {
	fetchRows(definition: ReportDefinitionV1Tasks, page: {offset: number; limit: number}): Promise<ReportDataResult>;
	/** Persists the definition as a first-class entry in the Reports list and resolves with the
	 * reference to it. Pass `saved` as null on the first save to create the report; pass the
	 * reference an earlier save resolved with to update that same report rather than create a
	 * duplicate. `promptText` is the request the report was generated from and is recorded only on
	 * the create; an update leaves the stored value alone. Rejects when the save fails — the caller
	 * must handle the rejection, because the report is then still unsaved and the surface has to say
	 * so. */
	saveDefinition(
		definition: ReportDefinitionV1Tasks,
		saved: SavedReportRef | null,
		promptText: string | null,
		manuallyEdited: boolean
	): Promise<SavedReportRef>;
	/** Loads a saved report's definition, plus its provenance, so the builder can re-open it. Rejects
	 * when the report cannot be read (missing, not permitted, or transport failure) — the caller has no
	 * definition to render and must say so rather than showing an empty builder. */
	loadDefinition(savedReportId: string): Promise<LoadedReport>;
	/** Exports the definition's full (unpaginated) result set as a CSV, honouring the active
	 * client-side grouping's sort order, and hands the file to the browser's download flow.
	 * `hiddenColumnPaths` are the columns switched off in ColumnManager, which the file leaves out so it
	 * matches the table on screen; hiding is live table state, so it cannot be read off the definition.
	 * Rejects when the export fails — the caller must handle the rejection, because no file was
	 * ever handed to the browser. Rejects with `ReportExportTooLargeError` when the backend refused the
	 * export outright for having too many rows, which is the one failure a retry cannot get past. */
	exportCsv(
		definition: ReportDefinitionV1Tasks,
		grouping: GroupingToken[],
		hiddenColumnPaths?: readonly string[]
	): Promise<void>;
	/** Opens an edit session, so readers keep executing the published version while the working copy
	 * diverges. A report with no published version cannot open one, and resolves `UNPUBLISHED`.
	 * Rejects on transport failure. */
	enterDraft(savedReportId: string): Promise<ReportLifecycleState>;
	/** Makes the working copy the version readers execute and closes the edit session. Rejects on
	 * transport failure. */
	publish(savedReportId: string): Promise<ReportLifecycleState>;
	/** Drops the edit session, reverting the drafted columns, name, period and grouping to the
	 * baseline captured when the session opened. Rejects on transport failure. */
	discard(savedReportId: string): Promise<ReportLifecycleState>;
	/** Persists a row's favorite state via the same task-favourite mechanism used elsewhere in the
	 * app (My Work, etc.) — the report builder does not have its own favourite concept. Rejects on
	 * transport failure; the caller is responsible for reverting any optimistic UI update. */
	toggleFavorite(taskId: string, favoured: boolean): Promise<void>;
}
